/*
 * Sidebar link to the Inbox, with a dot when there is something unread.
 *
 * "Inbox", not "Notifications" — a deliberately different word from the fleet page. Notifications
 * is what the fleet noticed about the estate; the Inbox is messages addressed to you. A distinct
 * icon (an envelope, not the bell) so the two are told apart at a glance.
 *
 * The dot reads Backstage's own notifications backend (`/api/notifications/status`) through the
 * discovery + fetch APIs, which carry the signed-in user's session. Read once on mount, not polled:
 * a dot that appears while you are reading an unrelated page is a notification system of its own,
 * and this is a signpost. When it cannot ask, it shows nothing — the page is where "could not ask"
 * is said out loud.
 */
import { useEffect, useState } from 'react';
import { SidebarItem } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import MailOutlineIcon from '@material-ui/icons/MailOutline';

import { INBOX_PATH } from './index';

export function InboxNavItem() {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [unread, setUnread] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const base = await discovery.getBaseUrl('notifications');
        const res = await fetchApi.fetch(`${base}/status`);
        if (!res.ok) return;
        const body = (await res.json()) as { unread?: number };
        if (!cancelled) setUnread((body.unread ?? 0) > 0);
      } catch {
        // Silent: a dot is a courtesy, and the page says "could not ask" out loud.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discovery, fetchApi]);

  return (
    <SidebarItem icon={MailOutlineIcon} to={INBOX_PATH} text="Inbox" hasNotifications={unread} />
  );
}
